import { Repeat, X } from 'lucide-react';
import type { Condition, LogicalOperator, OperatorType,  } from './nestedRuleFunctions';
import { useEffect, useState } from 'react';
import type { DataTypeItem } from '@/shared/partnerschema';
import { useGetAttributes, useGetAttributes2 } from '@/hooks/useLocalStorage';
import DropdownSelect from './DropdownSelect';

interface RuleConditionProps {
    condition: Condition;
    groupId: string;
    index: number;
    groupOperators: LogicalOperator[];
    isFirstInGroup: boolean;
    dataType: DataTypeItem[];
    operators: OperatorType[];
    onUpdateCondition: (condId: string, field: string, value: any) => void;
    onUpdateDatatype: (condId: string, field: string, value: any) => void;
    onUpdateLogicalOperator: (groupId: string, index: number, operator: LogicalOperator) => void;
    onDeleteCondition: (groupId: string, condId: string) => void;
    version: boolean;
}

const isNumericString = (val: string | undefined): boolean => {
    if (!val || val.trim() === '') return false;
    return !isNaN(Number(val)) && isFinite(Number(val));
};

const computeStoredValue = (base: string, adjustment: string, usePercentage: boolean): string => {
    const b = parseFloat(base) || 0;
    const a = parseFloat(adjustment);
    if (adjustment === '' || isNaN(a)) return base;
    const result = usePercentage ? b + (b * a) / 100 : b + a;
    return String(result);
};

const PERCENTAGE_OPTIONS = [
    { value: 'flat', label: '+ Flat' },
    { value: 'percent', label: '+ %' },
];

const RuleCondition = ({
    condition,
    groupId,
    index,
    groupOperators,
    isFirstInGroup,
    dataType,
    onUpdateCondition,
    onUpdateDatatype,
    onUpdateLogicalOperator,
    onDeleteCondition,
    version,
}: RuleConditionProps) => {
    const [dt1Code, setDt1Code] = useState<string>('');
    const [dt2Code, setDt2Code] = useState<string>('');

    const [baseValue, setBaseValue] = useState<string>('');
    const [adjustment, setAdjustment] = useState<string>('');
    const [adjustmentType, setAdjustmentType] = useState<'flat' | 'percent'>('flat');

    // ── TanStack: fetch attributes per selected datatype ──────────────────────
    const { data: attrData1 } = useGetAttributes(dt1Code);
    const { data: attrData2 } = useGetAttributes2(dt2Code);

    const blockedTypes = [9, 10, 11];

    const attributeOptions1 = (attrData1?.items ?? [])
        .filter(attr => attr?.type === undefined || !blockedTypes.includes(Number(attr.type)))
        .map(attr => ({ value: attr?.name || '', label: attr?.name || 'Unknown' }));

    const attributeOptions2 = (attrData2?.items ?? [])
        .filter(attr => attr?.type === undefined || !blockedTypes.includes(Number(attr.type)))
        .map(attr => ({ value: attr?.name || '', label: attr?.name || 'Unknown' }));

    // Sync baseValue from condition.value on external change
    useEffect(() => {
        if (condition.valueType === 'value' && isNumericString(condition.value)) {
            setBaseValue(prev => {
                const computed = computeStoredValue(prev, adjustment, adjustmentType === 'percent');
                if (computed !== condition.value) {
                    setAdjustment('');
                    setAdjustmentType('flat');
                    return condition.value ?? '';
                }
                return prev;
            });
        }
    }, [condition.id]);

    const dataTypeOptions = dataType.map(dt => ({
        value: dt?.Name || '',
        label: dt?.Name || 'Unknown',
    }));

    const operatorOptions = [
        { value: '==', label: 'Equals (==)' },
        { value: '!=', label: 'Not Equals (!=)' },
        { value: '>', label: 'Greater Than (>)' },
        { value: '<', label: 'Less Than (<)' },
        { value: '>=', label: 'Greater or Equal (>=)' },
        { value: '<=', label: 'Less or Equal (<=)' },
    ];

    const logicalOperatorOptions = [
        { value: 'AND', label: 'AND' },
        { value: 'OR', label: 'OR' },
    ];

    const showAdjustment = condition.valueType === 'value' && isNumericString(baseValue || condition.value);

    const handleNumericChange = (newBase: string, newAdj: string, newType: 'flat' | 'percent') => {
        const stored = computeStoredValue(newBase, newAdj, newType === 'percent');
        onUpdateCondition(condition.id, 'value', stored);
    };

    return (
        <div className="space-y-2">
            {(!isFirstInGroup && groupId !== 'root') && (
                <div className="flex items-center gap-2 text-sm text-gray-600 ml-4">
                    <DropdownSelect
                        value={groupOperators[index - 1] || 'AND'}
                        onValueChange={(value) =>
                            onUpdateLogicalOperator(groupId, index - 1, value as LogicalOperator)
                        }
                        options={logicalOperatorOptions}
                        triggerClassName="text-sm px-2 py-1 min-w-[80px]"
                    />
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2 bg-white p-2 rounded border">
                {/* DataType 1 */}
                <DropdownSelect
                    key={`dt1-${condition.id}-${condition.dataType}`}
                    value={condition.dataType}
                    onValueChange={(value) => {
                        const selectedDt = dataType.find(x => x.Name === value);
                        onUpdateDatatype(condition.id, 'dataType', selectedDt);
                        setDt1Code(String(selectedDt?.ID ?? ''));
                    }}
                    options={dataTypeOptions}
                    placeholder="Select DataType"
                    disabled={Boolean(version)}
                />

                {/* Attribute 1 */}
                <DropdownSelect
                    value={condition.attribute}
                    onValueChange={(value) => onUpdateCondition(condition.id, 'attribute', value)}
                    options={attributeOptions1}
                    placeholder={
                        condition.dataType
                            ? condition.attribute
                                ? condition.attribute
                                : attributeOptions1.length === 0
                                    ? 'No Attributes'
                                    : 'Select Attribute'
                            : 'Select Attribute'
                    }
                    disabled={Boolean(
                        version ||
                        !condition.dataType ||
                        (attributeOptions1.length === 0 && !condition.attribute)
                    )}
                />

                {/* Operator */}
                <DropdownSelect
                    value={condition.operator}
                    onValueChange={(value) => onUpdateCondition(condition.id, 'operator', value)}
                    options={operatorOptions}
                    triggerClassName="w-[195px]"
                    disabled={Boolean(version)}
                />

                {/* Value / Operand section */}
                {condition.valueType === 'operand' ? (
                    <>
                        <DropdownSelect
                            value={condition.dataType2 ?? ''}
                            onValueChange={(value) => {
                                const selectedDt = dataType.find(x => x.Name === value);
                                onUpdateDatatype(condition.id, 'dataType2', selectedDt);
                                setDt2Code(String(selectedDt?.ID ?? ''));
                            }}
                            options={dataTypeOptions}
                            placeholder="Select Type"
                        />

                        <DropdownSelect
                            key={`dt2-${condition.id}-${condition.dataType2}`}
                            value={condition.attribute2 ?? ''}
                            onValueChange={(value) => onUpdateCondition(condition.id, 'attribute2', value)}
                            options={attributeOptions2}
                            placeholder={
                                condition.dataType2
                                    ? condition.attribute2
                                        ? condition.attribute2
                                        : attributeOptions2.length === 0
                                            ? 'No Attributes'
                                            : 'Select Attribute'
                                    : 'Select Attribute'
                            }
                            disabled={Boolean(
                                !condition.dataType2 ||
                                (attributeOptions2.length === 0 && !condition.attribute2)
                            )}
                        />

                        <button
                            onClick={() => {
                                setBaseValue('');
                                setAdjustment('');
                                setAdjustmentType('flat');
                                onUpdateCondition(condition.id, 'valueType', 'value');
                            }}
                            className="p-2 hover:bg-gray-100 rounded"
                            title="Switch to value input"
                        >
                            <Repeat className="w-4 h-4" />
                        </button>
                    </>
                ) : (
                    <>
                        {showAdjustment ? (
                            <>
                                <input
                                    type="number"
                                    value={baseValue !== '' ? baseValue : condition.value || ''}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setBaseValue(val);
                                        handleNumericChange(val, adjustment, adjustmentType);
                                    }}
                                    placeholder="Enter value"
                                    className="border rounded px-3 py-2 w-[150px]"
                                />
                                <span className="text-sm text-gray-500 font-medium whitespace-nowrap">
                                    Price Adj.
                                </span>
                                <input
                                    type="number"
                                    value={adjustment}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setAdjustment(val);
                                        handleNumericChange(
                                            baseValue || condition.value || '',
                                            val,
                                            adjustmentType
                                        );
                                    }}
                                    placeholder="Adjustment"
                                    className="border rounded px-3 py-2 w-[130px]"
                                />
                                <DropdownSelect
                                    value={adjustmentType}
                                    onValueChange={(val) => {
                                        const t = val as 'flat' | 'percent';
                                        setAdjustmentType(t);
                                        handleNumericChange(
                                            baseValue || condition.value || '',
                                            adjustment,
                                            t
                                        );
                                    }}
                                    options={PERCENTAGE_OPTIONS}
                                    triggerClassName="w-[100px]"
                                />
                            </>
                        ) : (
                            <input
                                type="text"
                                value={condition.value || ''}
                                onChange={(e) =>
                                    onUpdateCondition(condition.id, 'value', e.target.value)
                                }
                                placeholder="Enter value"
                                className="border rounded px-3 py-2 w-[250px]"
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default RuleCondition;